// Client-side navigation intent resolver for the AI Assistant ChatBar.
//
// The SESSION agent behind the ChatBar can only query transaction data + RAG;
// it has NO app-navigation capability. Sending "navigate to X" phrases to the
// LLM produced ungrounded, non-deterministic replies (hallucinated example.com
// URLs, fabricated metrics) and a single such turn poisoned the backend session
// so later real data questions also failed. We resolve navigation intent
// locally instead and drive react-router directly, so the "Go somewhere"
// starters do what they say and never reach the LLM.

export type NavResolution =
	| { kind: "navigate"; route: string; label: string }
	| { kind: "needs-company"; label: string }
	| null;

interface PageDef {
	label: string;
	// Route builder. `companyId` is guaranteed non-null when `needsCompany`.
	route: (companyId: number) => string;
	needsCompany: boolean;
	// Normalized keyword phrases (lowercase, no punctuation) that name this page.
	keywords: string[];
}

// Order matters only for readability; matching is by exact keyword membership.
const PAGES: readonly PageDef[] = [
	// Company-scoped pages (mirror CompanyMenu). Require a company in context.
	// Labels follow the sidebar names (EQ-2434); the old names stay as keywords.
	{
		label: "Analytics Dashboard",
		needsCompany: true,
		route: (id) => `/company/${id}/dashboard`,
		keywords: ["analytics dashboard", "dashboard", "home"],
	},
	{
		label: "Financial Statements",
		needsCompany: true,
		route: (id) => `/company/${id}/analytics`,
		keywords: ["financial statements", "analytics"],
	},
	{
		label: "Financial Review",
		needsCompany: true,
		route: (id) => `/company/${id}/review`,
		keywords: ["financial review", "review"],
	},
	{
		label: "Reclassify",
		needsCompany: true,
		route: (id) => `/company/${id}/reclassify`,
		keywords: ["reclassify", "reclassification"],
	},
	{
		label: "AutoRec",
		needsCompany: true,
		route: (id) => `/company/${id}/reconciles`,
		keywords: ["autorec", "auto rec", "advanced reconciliation"],
	},
	// AutoRec sub-pages. Without these, "take me to schedules" fell through to
	// the LLM — which has no navigation tool, so it answered with a data dump
	// and left the user on the page they asked to leave. Each entry mirrors a
	// real child route under `reconciles` in Routes.tsx.
	{
		label: "Reconciliation Dashboard",
		needsCompany: true,
		route: (id) => `/company/${id}/reconciles/dashboard`,
		keywords: [
			"reconciliation dashboard",
			"autorec dashboard",
			// The "New Reconciliation" button lives in the AutoRec header, so this
			// is where a user asking to start one needs to land.
			"new reconciliation",
		],
	},
	{
		label: "Reconciliation Results",
		needsCompany: true,
		route: (id) => `/company/${id}/reconciles/results`,
		keywords: ["reconciliation results", "match results", "results"],
	},
	{
		label: "Matching Rules",
		needsCompany: true,
		route: (id) => `/company/${id}/reconciles/rules`,
		keywords: ["matching rules", "reconciliation rules", "rules"],
	},
	{
		label: "Mapping Rules",
		needsCompany: true,
		route: (id) => `/company/${id}/reconciles/mapping-rules`,
		keywords: ["mapping rules", "mapping rule"],
	},
	{
		label: "Reconciliation Exceptions",
		needsCompany: true,
		route: (id) => `/company/${id}/reconciles/exception`,
		keywords: ["exceptions", "exception", "exception dashboard"],
	},
	{
		label: "Reconciliation History",
		needsCompany: true,
		route: (id) => `/company/${id}/reconciles/history`,
		keywords: ["reconciliation history", "history"],
	},
	{
		label: "Reconciliation Schedules",
		needsCompany: true,
		route: (id) => `/company/${id}/reconciles/schedules`,
		keywords: [
			"schedules",
			"schedule",
			"reconciliation schedules",
			"scheduled reconciliations",
		],
	},
	{
		label: "Schedule Health",
		needsCompany: true,
		route: (id) => `/company/${id}/reconciles/schedule-health`,
		keywords: ["schedule health", "schedule monitoring"],
	},
	{
		label: "Account Reconciliations",
		needsCompany: true,
		route: (id) => `/company/${id}/reconcile`,
		keywords: [
			"account reconciliation",
			"account reconciliations",
			"classic reconciliation",
			"reconcile",
			"reconciliation",
			"reconciliations",
		],
	},
	{
		label: "Human Assist",
		needsCompany: true,
		route: (id) => `/company/${id}/services`,
		keywords: ["human assist", "services"],
	},
	{
		label: "File Converter",
		needsCompany: true,
		route: (id) => `/company/${id}/files`,
		keywords: ["file converter", "files"],
	},
	{
		label: "Platform Integrations",
		needsCompany: true,
		route: (id) => `/company/${id}/settings`,
		keywords: [
			"platform integrations",
			"settings & integrations",
			"settings and integrations",
			"company settings",
			"settings",
		],
	},
	// Global pages (no company needed).
	{
		label: "All Companies",
		needsCompany: false,
		route: () => "/",
		keywords: ["all companies", "companies"],
	},
	{
		label: "Team Management",
		needsCompany: false,
		route: () => "/team",
		keywords: ["team", "team management"],
	},
	{
		label: "Document Center",
		needsCompany: false,
		route: () => "/document-center",
		keywords: ["document center", "documents"],
	},
	{
		label: "Support",
		needsCompany: false,
		route: () => "/support",
		keywords: ["support"],
	},
	{
		label: "Help",
		needsCompany: false,
		route: () => "/help",
		keywords: ["help"],
	},
	{
		label: "Account",
		needsCompany: false,
		route: () => "/account",
		keywords: ["account", "billing"],
	},
];

// Longest first so "go to" is stripped before "go" would be (defensive; "go"
// alone is not a verb here, but keeps the scan order intent-explicit).
const NAV_VERBS: readonly string[] = [
	"navigate to",
	"take me to",
	"bring me to",
	"go to",
	"open",
	"show me",
	"show",
	"view",
	"see",
	"goto",
];

function normalize(raw: string): string {
	return raw
		.trim()
		.toLowerCase()
		.replace(/[.!?]+$/g, "")
		.replace(/\s+/g, " ");
}

function stripLeadingVerb(text: string): {
	remainder: string;
	hadVerb: boolean;
} {
	for (const verb of NAV_VERBS) {
		if (text === verb) return { remainder: "", hadVerb: true };
		if (text.startsWith(`${verb} `)) {
			return { remainder: text.slice(verb.length + 1), hadVerb: true };
		}
	}
	return { remainder: text, hadVerb: false };
}

function cleanTarget(text: string): string {
	return text
		.replace(/^(the|my|our)\s+/, "")
		.replace(/\s+(page|screen|tab|section)$/, "")
		.trim();
}

/**
 * Resolve a chat message to a navigation action, or `null` if it isn't a
 * navigation request (in which case the caller should send it to the LLM).
 *
 * A message navigates when EITHER it starts with a navigation verb and the
 * remainder names a known page ("go to analytics"), OR the whole message is
 * itself a bare page name ("dashboard"). This keeps data questions such as
 * "show me the total of all deposits" out of navigation — the residual phrase
 * isn't a page name, so it falls through to the LLM.
 */
export function resolveNavigation(
	rawMessage: string,
	companyId: number | null,
): NavResolution {
	const normalized = normalize(rawMessage);
	if (!normalized) return null;

	const { remainder, hadVerb } = stripLeadingVerb(normalized);
	// With a verb, match the remainder; without one, only a bare page name.
	const target = cleanTarget(hadVerb ? remainder : normalized);
	if (!target) return null;

	const match = PAGES.find((page) => page.keywords.includes(target));
	if (!match) return null;

	if (match.needsCompany && companyId == null) {
		return { kind: "needs-company", label: match.label };
	}
	// companyId is non-null here when needsCompany; global pages ignore it.
	return {
		kind: "navigate",
		route: match.route(companyId as number),
		label: match.label,
	};
}
